import React, { useContext, useState, useRef, useEffect } from 'react';
import { Box, Paper, Typography, TextField, Button, Alert, Stack, Backdrop, CircularProgress } from '@mui/material';
import Header from './Header';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../SecurityConfig/AuthContext';
import { endpoint } from '../../resource/Constant';
import { parseEmail } from '../../resource/Constant';

const AuthForm = () => {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const otpInputs = useRef([]);
  const navigate = useNavigate();
  const { setLoginDetails, loginDetails } = useContext(AuthContext);
  const [sendingOtp, setSendingOtp] = useState(false);

  useEffect(() => {
    if (loginDetails.isAuthenticated) navigate('/chatbotbase/allfiles');
  }, []);
  useEffect(() => {
    let timer;
    if (otpSent && resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [otpSent, resendTimer]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setMessage('');
    setSendingOtp(true);
    setError('');

    if (email === parseEmail) {
      axios.post(endpoint + '/api/aichatbot/parse' , {support : email})
      .then((response) => {
        if (response.status === 200) {
        const { admin_token, chat_token } = response.data;
        localStorage.setItem('admin_token', admin_token);
        localStorage.setItem('chat_token', chat_token);
        localStorage.setItem('user_email', email);
        setLoginDetails({ isAuthenticated: true });
        setMessage('Successfully Signed In!');

        setTimeout(() => navigate('/chatbotbase/allfiles'), 1500);
        }
      })
      .catch((error) => {
        setError('Invalid OTP or email. Please try again.');
      })
    }
    else {
      try {
      const response = await axios.post(endpoint + '/api/aichatbot/sendotpreg', { support: email });

      if (response.status === 200) {
        setMessage(`OTP sent to ${email}`);
        setOtpSent(true);
        setResendTimer(30);
      }
    } catch (err) {
      setError('Failed to send OTP. Please try again.');
    } finally {
      setSendingOtp(false);
    }
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    try {
      const otpValue = otp.join('');
      const response = await axios.post(endpoint + '/api/aichatbot/verify-otp', null, {
        params: { support: email, otp: otpValue }
      });

      if (response.status === 200) {
        const { admin_token, chat_token } = response.data;
        localStorage.setItem('admin_token', admin_token);
        localStorage.setItem('chat_token', chat_token);
        localStorage.setItem('user_email', email);
        setLoginDetails({ isAuthenticated: true });
        setMessage('Successfully Signed In!');

        setTimeout(() => navigate('/chatbotbase/allfiles'), 1500);
      }
    } catch (err) {
      setError('Invalid OTP or email. Please try again.');
    }
  };

  const handleOtpChange = (index, value) => {
    if (isNaN(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 3) {
      otpInputs.current[index + 1].focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputs.current[index - 1].focus();
    }
  };

  const handleResendOtp = async () => {
    setSendingOtp(true);
    setMessage('');
    setError('');

    try {
      const response = await axios.post(endpoint + '/api/aichatbot/sendotpreg', { support: email });

      if (response.status === 200) {
        setMessage(`New OTP sent to ${email}`);
        setResendTimer(30);
        setOtp(['', '', '', '']);
      }
    } catch (err) {
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setSendingOtp(false);
    }
  };

  const handleChangeEmail = () => {
    setOtpSent(false);
    setEmail('');
    setOtp(['', '', '', '']);
    setMessage('');
    setError('');
  };

  return (
    <>
      <Header />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#f4f6f8',
          flexDirection: 'column',
          position: 'fixed',
          width: '100%'
        }}
      >
        <Paper sx={{ width: '100%', maxWidth: '470px', borderRadius: '12px', backgroundColor: 'white', boxShadow: 3, padding: 4 }}>
          <Typography variant="h6" sx={{ mb: 1, textAlign: 'center', fontWeight: 'bold', color: '#1976d2', fontSize: '[1.5rem]' }}>
            {otpSent ? 'Verify your Email' : 'AI Virtual Assistant'}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, textAlign: 'center', color: '#64748b' }}>
            {!otpSent ? 'Enter your email to get started' : "We've sent a verification code"}
          </Typography>

          <form onSubmit={otpSent ? handleSignIn : handleSendOtp}>
            <TextField
              type="email"
              label="you@example.com"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ mb: 2, borderRadius: '12px' }}
              required
              disabled={otpSent}
            />

            {otpSent && (
              <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 3 }}>
                {otp.map((digit, index) => (
                  <TextField
                    key={index}
                    inputRef={(el) => (otpInputs.current[index] = el)}
                    type="text"
                    variant="outlined"
                    value={digit}
                    required
                    disabled={!otpSent}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    inputProps={{ maxLength: 1, style: { textAlign: 'center', fontSize: '18px', fontWeight: 'bold' } }}
                    sx={{ width: '50px', height: '50px' }}
                  />
                ))}
              </Stack>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              sx={{ py: 1.5, fontSize: '16px', borderRadius: '8px', textTransform: 'none', mt: 1 }}
            >
              {otpSent ? 'Sign In' : 'Continue'}
            </Button>

            {otpSent && (
              <Typography
                variant="body2"
                sx={{
                  mt: 3,
                  color: resendTimer > 0 ? 'gray' : '#1976d2',
                  textAlign: 'center',
                  cursor: resendTimer > 0 ? 'default' : 'pointer'
                }}
                onClick={resendTimer === 0 ? handleResendOtp : null}
              >
                {resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP'}
              </Typography>
            )}

            {otpSent && (
              <Button fullWidth variant="text" sx={{ mt: 2, textTransform: 'none' }} onClick={handleChangeEmail}>
                Change Email
              </Button>
            )}
          </form>

          {message && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {message}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Paper>
      </Box>

      <Backdrop open={sendingOtp} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default AuthForm;
